import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  BROWSER_SESSION_EXPIRED_EVENT,
  BROWSER_SESSION_ROTATED_EVENT,
  bootstrapBrowserSession,
  login,
  logoutBrowserSession,
  messageCacheManager,
  refreshBrowserSession,
  startBrowserSessionCoordination,
  synchronizeBrowserSessionAfterRotation,
  unifiedWebSocketManager,
  type BrowserSession,
} from '../services/api.ts';
import {
  getPlatformAvailabilitySnapshot,
  isPlatformAvailabilityError,
  subscribePlatformAvailability,
} from '../services/platformAvailability.ts';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  status: AuthStatus;
  session: BrowserSession | null;
  signIn: (email: string, password: string) => Promise<BrowserSession>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<BrowserSession>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function isTransientSessionFailure(error: unknown): boolean {
  if (isPlatformAvailabilityError(error)) return true;
  const failure = error as {
    isAxiosError?: boolean;
    code?: string;
    response?: { status?: number };
  } | null;
  if (!failure?.isAxiosError || failure.code === 'ERR_CANCELED') return false;
  if (!failure.response) return true;
  const status = failure.response.status || 0;
  return (status >= 500 && status < 600) || status === 408 || status === 429;
}

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [session, setSession] = useState<BrowserSession | null>(null);
  const sessionRevisionRef = useRef(0);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let pendingRetry: (() => void) | undefined;

    const clearRetry = () => {
      clearTimeout(retryTimer);
      retryTimer = undefined;
      pendingRetry = undefined;
    };

    const applySession = (
      nextSession: BrowserSession | null,
      expectedRevision: number,
    ) => {
      if (!mountedRef.current || expectedRevision !== sessionRevisionRef.current) {
        return;
      }
      setSession(nextSession);
      setStatus(nextSession ? 'authenticated' : 'anonymous');
    };

    const handleSessionExpired = () => {
      sessionRevisionRef.current += 1;
      clearRetry();
      messageCacheManager.clearAllCache();
      unifiedWebSocketManager.disconnect();
      setSession(null);
      setStatus('anonymous');
    };

    const checkSession = (
      check: () => Promise<BrowserSession | null>,
      expectedRevision: number,
    ) => {
      if (!mountedRef.current || expectedRevision !== sessionRevisionRef.current) return;
      clearRetry();
      void check()
        .then((checkedSession) => applySession(checkedSession, expectedRevision))
        .catch((error) => {
          if (!mountedRef.current || expectedRevision !== sessionRevisionRef.current) return;
          if (error?.isAxiosError === true && error.response?.status === 401) {
            handleSessionExpired();
            return;
          }
          if (!isTransientSessionFailure(error)) {
            // Conserva o resultado anterior para falhas definitivas de bootstrap,
            // sem retirar uma sessão que já foi confirmada nem repetir sem fim.
            setStatus((current) => current === 'loading' ? 'anonymous' : current);
            return;
          }
          // Falha de disponibilidade não comprova expiração: mantém a sessão
          // confirmada (ou a validação inicial) até uma resposta autoritativa.
          pendingRetry = () => checkSession(check, expectedRevision);
          retryTimer = setTimeout(() => pendingRetry?.(), 5_000);
        });
    };

    const handleSessionRotated = () => {
      checkSession(synchronizeBrowserSessionAfterRotation, ++sessionRevisionRef.current);
    };

    window.addEventListener(BROWSER_SESSION_EXPIRED_EVENT, handleSessionExpired);
    window.addEventListener(BROWSER_SESSION_ROTATED_EVENT, handleSessionRotated);
    const stopSessionCoordination = startBrowserSessionCoordination();
    const stopAvailabilitySubscription = subscribePlatformAvailability(() => {
      if (getPlatformAvailabilitySnapshot().ready) pendingRetry?.();
    });
    checkSession(bootstrapBrowserSession, sessionRevisionRef.current);

    return () => {
      mountedRef.current = false;
      sessionRevisionRef.current += 1;
      clearRetry();
      stopAvailabilitySubscription();
      stopSessionCoordination();
      window.removeEventListener(BROWSER_SESSION_EXPIRED_EVENT, handleSessionExpired);
      window.removeEventListener(BROWSER_SESSION_ROTATED_EVENT, handleSessionRotated);
      unifiedWebSocketManager.disconnect();
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const expectedRevision = ++sessionRevisionRef.current;
    const authenticatedSession = await login(email, password);
    if (!mountedRef.current || expectedRevision !== sessionRevisionRef.current) {
      throw new Error('A sessão mudou. Confira seu acesso antes de continuar.');
    }
    setSession(authenticatedSession);
    setStatus('authenticated');
    return authenticatedSession;
  }, []);

  const signOut = useCallback(async () => {
    const expectedRevision = ++sessionRevisionRef.current;
    await logoutBrowserSession();
    if (!mountedRef.current || expectedRevision !== sessionRevisionRef.current) return;
    setSession(null);
    setStatus('anonymous');
  }, []);

  const refreshSession = useCallback(async () => {
    const expectedRevision = sessionRevisionRef.current;
    const refreshedSession = await refreshBrowserSession();
    if (!mountedRef.current || expectedRevision !== sessionRevisionRef.current) {
      throw new Error('A sessão mudou. Confira seu acesso antes de continuar.');
    }
    setSession(refreshedSession);
    setStatus('authenticated');
    return refreshedSession;
  }, []);

  const value = useMemo(
    () => ({ status, session, signIn, signOut, refreshSession }),
    [refreshSession, session, signIn, signOut, status],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider.');
  }
  return context;
}
